import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Layers,
  AlertTriangle,
  ArrowRight,
  PlusCircle,
  Truck,
  ArrowDownLeft,
  Skull,
  Tag,
  Wheat,
  ArrowRightLeft,
  Receipt,
  Activity,
  RotateCcw,
  CheckCircle2,
  Calendar,
  Users,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState, Skeleton } from '@/components/common/LoadingState';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/utils';
import { ProductionCycleRecord } from '@/types/database';
import { PigArrivalModal } from '@/components/cycles/PigArrivalModal';
import { MortalityLogModal } from '@/components/cycles/MortalityLogModal';
import { BulkFeedInvoiceModal } from '@/components/feed/BulkFeedInvoiceModal';
import { FeedTransferModal } from '@/components/feed/FeedTransferModal';
import { PigSaleModal } from '@/components/sales/PigSaleModal';
import { CycleExpenseModal } from '@/components/expenses/CycleExpenseModal';

interface ContextType {
  selectedFarmId: string | null;
}

export interface DashboardKpiData {
  activeCycles: number;
  draftSettlementCycles: number;
  totalPigsDelivered: number;
  needsReviewIssues: number;
}

export interface RecentActivityItem {
  id: string;
  type: 'sale' | 'feed' | 'arrival' | 'mortality';
  title: string;
  subtitle: string;
  date: string;
  amountOrCount: string;
  cycleCode?: string;
  cycleId?: string;
}

export const DashboardPage: React.FC = () => {
  const { selectedFarmId } = useOutletContext<ContextType>();
  const { isOperations } = useAuth();
  const navigate = useNavigate();

  const [kpis, setKpis] = useState<DashboardKpiData>({
    activeCycles: 0,
    draftSettlementCycles: 0,
    totalPigsDelivered: 0,
    needsReviewIssues: 0,
  });
  const [activeCycles, setActiveCycles] = useState<ProductionCycleRecord[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivityItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Quick Action Modal States
  const [showArrivalModal, setShowArrivalModal] = useState<boolean>(false);
  const [showMortalityModal, setShowMortalityModal] = useState<boolean>(false);
  const [showBulkFeedModal, setShowBulkFeedModal] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [showSaleModal, setShowSaleModal] = useState<boolean>(false);
  const [showExpenseModal, setShowExpenseModal] = useState<boolean>(false);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Production Cycles
      let cyclesQuery = supabase
        .from('production_cycles')
        .select(`
          *,
          growers (full_name),
          farms (farm_name)
        `)
        .eq('is_void', false)
        .order('start_date', { ascending: false });

      if (selectedFarmId) {
        cyclesQuery = cyclesQuery.eq('farm_id', selectedFarmId);
      }

      const { data: cyclesData } = await cyclesQuery;
      const allCycles = (cyclesData || []) as ProductionCycleRecord[];

      const activeCount = allCycles.filter((c) => c.status === 'active' || c.status === 'selling').length;
      const totalDelivered = allCycles.reduce((sum, c) => sum + (Number(c.initial_head_count) || 0), 0);

      // 2. Fetch Draft Settlements count & Needs Review Issues count
      const [{ count: draftCount }, { count: issueCount }] = await Promise.all([
        supabase
          .from('settlements')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'draft')
          .eq('is_void', false),
        supabase
          .from('reconciliation_issues')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'needs_review'),
      ]);

      // 3. Fetch Recent Field Activity (Sales, Feed, Arrivals, Mortalities)
      const [salesRes, feedRes, arrivalsRes, mortRes] = await Promise.all([
        supabase
          .from('pig_sales')
          .select('id, sale_number, sale_date, total_amount, head_count, cycle_id, production_cycles(cycle_code, growers(full_name))')
          .eq('is_void', false)
          .order('sale_date', { ascending: false })
          .limit(5),
        supabase
          .from('feed_receipts')
          .select('id, receipt_number, invoice_no, receipt_date, quantity_bags, total_cost, cycle_id, feed_products(product_name), production_cycles(cycle_code, growers(full_name))')
          .eq('is_void', false)
          .order('receipt_date', { ascending: false })
          .limit(5),
        supabase
          .from('pig_arrivals')
          .select('id, arrival_number, arrival_date, head_count, cycle_id, production_cycles(cycle_code, growers(full_name))')
          .eq('is_void', false)
          .order('arrival_date', { ascending: false })
          .limit(5),
        supabase
          .from('mortality_events')
          .select('id, event_date, head_count, cause_of_death, cycle_id, production_cycles(cycle_code, growers(full_name))')
          .eq('is_void', false)
          .order('event_date', { ascending: false })
          .limit(5),
      ]);

      const activities: RecentActivityItem[] = [];

      (salesRes.data || []).forEach((s: any) => {
        activities.push({
          id: s.id,
          type: 'sale',
          title: `Pig Sale ${s.head_count ? `(${s.head_count} head)` : ''}`,
          subtitle: `${s.production_cycles?.cycle_code || 'Cycle'} • ${s.production_cycles?.growers?.full_name || 'Raiser'}`,
          date: s.sale_date,
          amountOrCount: `₱${Number(s.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          cycleCode: s.production_cycles?.cycle_code,
          cycleId: s.cycle_id,
        });
      });

      (feedRes.data || []).forEach((f: any) => {
        activities.push({
          id: f.id,
          type: 'feed',
          title: `Feed Delivery: ${f.feed_products?.product_name || 'Feeds'} (${f.quantity_bags} bags)`,
          subtitle: `${f.production_cycles?.cycle_code || 'Cycle'} • ${f.production_cycles?.growers?.full_name || 'Raiser'} ${f.invoice_no ? `(Inv #${f.invoice_no})` : ''}`,
          date: f.receipt_date,
          amountOrCount: `₱${Number(f.total_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          cycleCode: f.production_cycles?.cycle_code,
          cycleId: f.cycle_id,
        });
      });

      (arrivalsRes.data || []).forEach((a: any) => {
        activities.push({
          id: a.id,
          type: 'arrival',
          title: `Piglet Delivery Intake (+${a.head_count} head)`,
          subtitle: `${a.production_cycles?.cycle_code || 'Cycle'} • ${a.production_cycles?.growers?.full_name || 'Raiser'}`,
          date: a.arrival_date,
          amountOrCount: `+${a.head_count} head`,
          cycleCode: a.production_cycles?.cycle_code,
          cycleId: a.cycle_id,
        });
      });

      (mortRes.data || []).forEach((m: any) => {
        activities.push({
          id: m.id,
          type: 'mortality',
          title: `Mortality Logged (-${m.head_count} head)`,
          subtitle: `${m.production_cycles?.cycle_code || 'Cycle'} • ${m.cause_of_death || 'Reported'}`,
          date: m.event_date,
          amountOrCount: `-${m.head_count} head`,
          cycleCode: m.production_cycles?.cycle_code,
          cycleId: m.cycle_id,
        });
      });

      // Sort combined activities by date descending
      activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setKpis({
        activeCycles: activeCount,
        draftSettlementCycles: draftCount || 0,
        totalPigsDelivered: totalDelivered,
        needsReviewIssues: issueCount || 0,
      });

      // Filter active or recently started cycles for display
      setActiveCycles(allCycles.slice(0, 10));
      setRecentActivities(activities.slice(0, 8));
    } catch (err) {
      console.error('[DashboardPage] Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFarmId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleModalSuccess = () => {
    fetchDashboardData();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Standard PageHeader */}
      <PageHeader
        title="Operations Hub"
        subtitle="Daily swine farm operations, cycle lifecycles, and rapid transaction entries."
        icon={Activity}
        helpScreenKey="dashboard"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ActionTooltip content="Reload latest active herd counts, pending settlements, and recent field activities.">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDashboardData}
                disabled={loading}
                className="text-xs h-9 min-h-[44px] gap-1.5 border-slate-300 hover:bg-slate-50"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {isOperations && (
              <>
                <ActionTooltip content="Log incoming piglet delivery intake, placement weight, and DR receipt number.">
                  <Button
                    size="sm"
                    onClick={() => setShowArrivalModal(true)}
                    className="text-xs h-9 min-h-[44px] bg-emerald-700 hover:bg-emerald-800 text-white font-semibold gap-1.5 shadow-xs"
                  >
                    <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-200" />
                    <span>Pig Arrival</span>
                  </Button>
                </ActionTooltip>
                <ActionTooltip content="Record pig deaths, causes, and immediately decrease live herd headcounts.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowMortalityModal(true)}
                    className="text-xs h-9 min-h-[44px] border-rose-300 text-rose-900 bg-rose-50/60 hover:bg-rose-100 font-semibold gap-1.5"
                  >
                    <Skull className="w-3.5 h-3.5 text-rose-600" />
                    <span>Mortality</span>
                  </Button>
                </ActionTooltip>
                <ActionTooltip content="Allocate a multi-cycle supplier feed delivery invoice across active grower pens.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkFeedModal(true)}
                    className="text-xs h-9 min-h-[44px] border-amber-300 text-amber-950 bg-amber-50/60 hover:bg-amber-100 font-semibold gap-1.5"
                  >
                    <Wheat className="w-3.5 h-3.5 text-amber-700" />
                    <span>Bulk Feed</span>
                  </Button>
                </ActionTooltip>
                <ActionTooltip content="Move unused feed bags from one batch to another with zero net farm cost impact.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTransferModal(true)}
                    className="text-xs h-9 min-h-[44px] border-indigo-300 text-indigo-950 bg-indigo-50/60 hover:bg-indigo-100 font-semibold gap-1.5"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 text-indigo-700" />
                    <span>Transfer Feed</span>
                  </Button>
                </ActionTooltip>
                <ActionTooltip content="Open harvest scale weighing tickets, buyer liquidation, and gate passes.">
                  <Button
                    size="sm"
                    onClick={() => navigate('/sales')}
                    className="text-xs h-9 min-h-[44px] bg-slate-900 hover:bg-slate-800 text-white font-semibold gap-1.5 shadow-xs"
                  >
                    <Tag className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Pig Sale Grid</span>
                  </Button>
                </ActionTooltip>
              </>
            )}
          </div>
        }
      />

      {/* 2. Standard 4-Card KpiCardGroup */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Active Production Cycles"
          value={kpis.activeCycles.toString()}
          subtitle={kpis.activeCycles > 0 ? `${kpis.activeCycles} cycles growing on feed` : 'No active cycles'}
          icon={Layers}
          variant="default"
          loading={loading}
        />
        <KpiCard
          title="Pigs Delivered"
          value={kpis.totalPigsDelivered.toLocaleString()}
          subtitle="Total intake placement volume"
          icon={ArrowDownLeft}
          variant="emerald"
          loading={loading}
        />
        <KpiCard
          title="Draft Settlements"
          value={kpis.draftSettlementCycles.toString()}
          subtitle="Cycles with sales ready for liquidation"
          icon={Receipt}
          variant="indigo"
          loading={loading}
        />
        <div
          onClick={() => navigate('/admin/review-resolve')}
          className="cursor-pointer transition-transform active:scale-[0.99]"
        >
          <KpiCard
            title="Review & Resolve Queue"
            value={kpis.needsReviewIssues.toString()}
            subtitle={kpis.needsReviewIssues > 0 ? 'Items awaiting human review' : 'Zero unmapped ambiguities'}
            icon={AlertTriangle}
            variant={kpis.needsReviewIssues > 0 ? 'amber' : 'default'}
            loading={loading}
          />
        </div>
      </KpiCardGroup>

      {/* 3. Main Operational Split Grid: Active Production Cycles + Recent Field Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Production Cycles List (Batch - Raiser format) */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-slate-200 shadow-sm rounded-xl overflow-hidden">
            <CardHeader className="p-4 sm:p-5 flex flex-row items-center justify-between border-b border-slate-100 bg-white">
              <div>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-emerald-700" />
                  <span>Production Cycles ({activeCycles.length})</span>
                </CardTitle>
                <CardDescription className="text-xs mt-0.5 text-slate-500">
                  Standard <code>Batch - Raiser</code> format with live status lifecycle.
                </CardDescription>
              </div>
              <Button
                onClick={() => navigate('/cycles')}
                variant="ghost"
                size="sm"
                className="text-xs text-emerald-800 hover:text-emerald-950 hover:bg-emerald-50 gap-1 font-semibold min-h-[44px]"
              >
                <span>View All</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {loading ? (
                <div className="py-12 bg-white">
                  <LoadingState message="Loading operational cycles..." />
                </div>
              ) : activeCycles.length === 0 ? (
                <div className="py-12 bg-white">
                  <EmptyState
                    icon={Layers}
                    title="No Production Cycles Found"
                    description="No production cycles found for the selected farm filter."
                  />
                </div>
              ) : (
                <div>
                  {/* Desktop Table (md:table) */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse font-sans">
                      <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-3 px-4 min-w-[200px]">Production Cycle (Batch - Raiser)</th>
                          <th className="py-3 px-4 hidden sm:table-cell">Farm Location</th>
                          <th className="py-3 px-4 text-right">Delivered</th>
                          <th className="py-3 px-4 text-center">Lifecycle Status</th>
                          <th className="py-3 px-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {activeCycles.map((cycle) => (
                          <tr key={cycle.id} className="hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4">
                              <span className="font-mono font-bold text-slate-900 block">
                                {cycle.cycle_code}
                              </span>
                              <span className="text-[11px] text-slate-500 block">
                                {cycle.growers?.full_name || cycle.legacy_raiser_name || 'Company Owned'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600 hidden sm:table-cell">
                              {cycle.farms?.farm_name || 'Batangas'}
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-slate-900">
                              {Number(cycle.initial_head_count || 0).toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <StatusBadge status={cycle.status} category="cycle" />
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/cycles/${cycle.id}`)}
                                className="h-8 text-xs px-2.5 font-semibold text-emerald-800 hover:text-emerald-950 hover:bg-emerald-50"
                              >
                                View Cycle
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards (md:hidden) */}
                  <div className="md:hidden divide-y divide-slate-100 bg-white">
                    {activeCycles.map((cycle) => (
                      <div
                        key={cycle.id}
                        onClick={() => navigate(`/cycles/${cycle.id}`)}
                        className="p-4 hover:bg-slate-50 transition-colors cursor-pointer space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="font-mono font-bold text-xs text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                              {cycle.cycle_code}
                            </span>
                            <div className="font-bold text-sm text-slate-800 mt-1">
                              {cycle.growers?.full_name || cycle.legacy_raiser_name || 'Company Owned'}
                            </div>
                          </div>
                          <StatusBadge status={cycle.status} category="cycle" />
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                          <span>{cycle.farms?.farm_name || 'Batangas Farm'}</span>
                          <span className="font-mono font-bold text-slate-900">
                            {Number(cycle.initial_head_count || 0).toLocaleString()} head
                          </span>
                        </div>

                        <div className="pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full h-10 min-h-[44px] text-xs font-semibold text-emerald-800 border-emerald-200 bg-emerald-50/50 justify-center gap-1"
                          >
                            <span>Open Cycle Workspace</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Col: Recent Field Activity Feed */}
        <div className="space-y-4">
          <Card className="border-slate-200 shadow-sm rounded-xl overflow-hidden">
            <CardHeader className="p-4 border-b border-slate-100 bg-white">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-700" />
                <span>Recent Field Activity</span>
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Real-time operational stream from ledger & receipts.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0 bg-white">
              {loading ? (
                <div className="py-12">
                  <LoadingState message="Loading recent activities..." />
                </div>
              ) : recentActivities.length === 0 ? (
                <div className="py-12">
                  <EmptyState
                    icon={Activity}
                    title="No Recent Activity"
                    description="No recent field transactions logged."
                  />
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {recentActivities.map((act) => {
                    const isSale = act.type === 'sale';
                    const isFeed = act.type === 'feed';
                    const isArrival = act.type === 'arrival';

                    return (
                      <div
                        key={act.id}
                        onClick={() => act.cycleId && navigate(`/cycles/${act.cycleId}`)}
                        className="p-3.5 hover:bg-slate-50 transition-colors cursor-pointer flex items-start gap-3 text-xs"
                      >
                        <div
                          className={`p-2 rounded-lg shrink-0 mt-0.5 ${
                            isSale
                              ? 'bg-emerald-100 text-emerald-800'
                              : isFeed
                              ? 'bg-amber-100 text-amber-800'
                              : isArrival
                              ? 'bg-indigo-100 text-indigo-800'
                              : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {isSale ? (
                            <Tag className="w-3.5 h-3.5" />
                          ) : isFeed ? (
                            <Wheat className="w-3.5 h-3.5" />
                          ) : isArrival ? (
                            <ArrowDownLeft className="w-3.5 h-3.5" />
                          ) : (
                            <Skull className="w-3.5 h-3.5" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-bold text-slate-900 truncate">{act.title}</span>
                            <span className="font-mono font-bold text-slate-800 whitespace-nowrap">
                              {act.amountOrCount}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 truncate mt-0.5">{act.subtitle}</p>
                          <span className="text-[10px] text-slate-400 font-mono block mt-1">
                            {formatDate(act.date)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Action Modals */}
      {showArrivalModal && (
        <PigArrivalModal
          isOpen={true}
          onClose={() => setShowArrivalModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showMortalityModal && (
        <MortalityLogModal
          isOpen={true}
          onClose={() => setShowMortalityModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showBulkFeedModal && (
        <BulkFeedInvoiceModal
          isOpen={true}
          onClose={() => setShowBulkFeedModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showTransferModal && (
        <FeedTransferModal
          isOpen={true}
          onClose={() => setShowTransferModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showSaleModal && (
        <PigSaleModal
          isOpen={true}
          onClose={() => setShowSaleModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showExpenseModal && (
        <CycleExpenseModal
          isOpen={true}
          onClose={() => setShowExpenseModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
